import React, { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Minus, Plus, X } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";

/**
 * Phase 3 — Item Customization Sheet.
 *
 * Opens on either (a) direct call via `openForProduct(product_id)` or
 * (b) when `/cart/add` returns 409 "needs customization".
 *
 * Live price preview is client-computed from the server's `/products/{id}/full`
 * payload, but the authoritative total is always re-priced by the server on
 * `/cart/add-item` and again on `/orders`.
 */
export default function ItemCustomizeSheet({ productId, onClose }) {
  const { addCustomizedItem } = useCart();
  const [open, setOpen] = useState(Boolean(productId));
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const [variationName, setVariationName] = useState(null);
  // ingredient state: { [ingredient_id]: {mode, qty} }
  const [ingState, setIngState] = useState({});
  // modifier state: { [group_id]: { [option_id]: qty } }
  const [modState, setModState] = useState({});
  const [instructions, setInstructions] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [validationError, setValidationError] = useState(null);

  useEffect(() => {
    setOpen(Boolean(productId));
    if (!productId) {
      setProduct(null);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/products/${productId}/full`);
        setProduct(data);
        // Prefill defaults
        const dv = (data.variations || []).find((v) => v.is_default);
        setVariationName(dv ? dv.name : null);
        const ingInit = {};
        (data.ingredients || []).forEach((ing) => {
          ingInit[ing.ingredient_id] = { mode: "default", qty: 1 };
        });
        setIngState(ingInit);
        const modInit = {};
        (data.modifier_groups || []).forEach((g) => {
          const defaults = {};
          (g.options || []).forEach((o) => {
            if (o.is_default) defaults[o.option_id] = 1;
          });
          modInit[g.group_id] = defaults;
        });
        setModState(modInit);
        setInstructions("");
        setQuantity(1);
      } catch (e) {
        toast.error("Couldn't load this item");
        setOpen(false);
        onClose?.();
      } finally {
        setLoading(false);
      }
    })();
  }, [productId]);

  const basePriceCents = useMemo(() => {
    if (!product) return 0;
    const sp = product.sale_price;
    const price = Number(product.price || 0);
    if (sp != null && sp !== "" && Number(sp) > 0 && Number(sp) < price) {
      return Math.round(Number(sp) * 100);
    }
    return Math.round(price * 100);
  }, [product]);

  const livePriceCents = useMemo(() => {
    if (!product) return 0;
    let add = 0;
    // variation
    const vars = product.variations || [];
    const chosenVar = vars.find((v) => v.name === variationName);
    if (chosenVar) add += Number(chosenVar.price_delta_cents || chosenVar.price_delta || 0);
    // ingredients
    (product.ingredients || []).forEach((ing) => {
      const s = ingState[ing.ingredient_id];
      if (!s || s.mode !== "extra") return;
      const unit = Number(ing.extra_price_cents || 0);
      add += unit * (s.qty || 1);
    });
    // modifiers
    (product.modifier_groups || []).forEach((g) => {
      const groupSel = modState[g.group_id] || {};
      (g.options || []).forEach((o) => {
        const q = Number(groupSel[o.option_id] || 0);
        if (q) add += Number(o.price_adjust_cents || 0) * q;
      });
    });
    return Math.max(0, basePriceCents + add);
  }, [product, variationName, ingState, modState, basePriceCents]);

  const dollarFmt = (cents) => `$${(cents / 100).toFixed(2)}`;
  const signedFmt = (cents) => {
    if (cents === 0) return "Free";
    const sign = cents > 0 ? "+" : "−";
    return `${sign}${dollarFmt(Math.abs(cents))}`;
  };

  const close = () => {
    setOpen(false);
    onClose?.();
  };

  const buildSelection = () => ({
    variation_name: variationName,
    ingredients: Object.entries(ingState)
      .filter(([, s]) => s.mode !== "default")
      .map(([ingredient_id, s]) => ({
        ingredient_id,
        mode: s.mode,
        qty: s.qty || 1,
      })),
    modifiers: Object.entries(modState)
      .map(([group_id, opts]) => ({
        group_id,
        options: Object.entries(opts)
          .filter(([, q]) => Number(q) > 0)
          .map(([option_id, qty]) => ({ option_id, qty: Number(qty) })),
      }))
      .filter((g) => g.options.length > 0),
    special_instructions: instructions || null,
  });

  // Client validation preview — matches server rules so users see errors
  // before clicking Add. Server is still the source of truth.
  const groupValidation = useMemo(() => {
    if (!product) return {};
    const out = {};
    (product.modifier_groups || []).forEach((g) => {
      const picks = modState[g.group_id] || {};
      const totalQty = Object.values(picks).reduce((a, b) => a + Number(b || 0), 0);
      let msg = null;
      if (g.required && totalQty === 0) msg = `Pick an option`;
      else if (totalQty && totalQty < (g.min_select || 0)) msg = `Pick at least ${g.min_select}`;
      else if (totalQty > (g.max_select || 1)) msg = `Max ${g.max_select}`;
      out[g.group_id] = msg;
    });
    return out;
  }, [product, modState]);

  const canAdd = useMemo(() => {
    return Object.values(groupValidation).every((v) => !v);
  }, [groupValidation]);

  const onAdd = async () => {
    if (!product) return;
    setValidationError(null);
    setAdding(true);
    try {
      await addCustomizedItem(product.product_id, buildSelection(), quantity);
      toast.success(`${product.name} added to bag`);
      close();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't add to bag";
      setValidationError(typeof msg === "string" ? msg : "Please review your selections");
      toast.error(typeof msg === "string" ? msg : "Please review your selections");
    } finally {
      setAdding(false);
    }
  };

  const setIngredientMode = (iid, mode) => {
    setIngState((s) => ({ ...s, [iid]: { ...(s[iid] || { qty: 1 }), mode, qty: mode === "extra" ? (s[iid]?.qty || 1) : 1 } }));
  };
  const bumpExtra = (iid, delta, maxAmount) => {
    setIngState((s) => {
      const curr = s[iid] || { mode: "extra", qty: 0 };
      const nextQty = Math.max(1, Math.min(maxAmount, (curr.qty || 1) + delta));
      return { ...s, [iid]: { mode: "extra", qty: nextQty } };
    });
  };

  const toggleModOption = (groupId, optId, selectionType, maxSelect, maxQty) => {
    setModState((s) => {
      const curr = { ...(s[groupId] || {}) };
      const have = Number(curr[optId] || 0);
      if (selectionType === "single") {
        return { ...s, [groupId]: have ? {} : { [optId]: 1 } };
      }
      if (have) {
        delete curr[optId];
      } else {
        const totalPicked = Object.values(curr).reduce((a, b) => a + Number(b || 0), 0);
        if (totalPicked >= maxSelect) {
          toast.error(`Max ${maxSelect} selections in this group`);
          return s;
        }
        curr[optId] = 1;
      }
      return { ...s, [groupId]: curr };
    });
  };
  const bumpModQty = (groupId, optId, delta, maxQty, maxSelect) => {
    setModState((s) => {
      const curr = { ...(s[groupId] || {}) };
      const have = Number(curr[optId] || 0);
      const next = Math.max(0, Math.min(maxQty, have + delta));
      // Enforce group max across total qty
      if (delta > 0) {
        const totalPicked = Object.entries(curr).reduce(
          (a, [k, v]) => a + (k === optId ? next : Number(v || 0)),
          0,
        );
        if (totalPicked > maxSelect) {
          toast.error(`Max ${maxSelect} selections in this group`);
          return s;
        }
      }
      if (next === 0) delete curr[optId];
      else curr[optId] = next;
      return { ...s, [groupId]: curr };
    });
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <SheetContent
        side="right"
        className="bg-zinc-950 border-l border-white/10 text-white w-full sm:max-w-lg flex flex-col p-0"
        data-testid="item-customize-sheet"
      >
        <SheetHeader className="px-6 py-5 border-b border-white/10 flex-row items-start justify-between">
          <div className="flex-1 min-w-0">
            <SheetTitle className="text-xl font-black tracking-tight text-white truncate">
              {product?.name || (loading ? "Loading…" : "Customize")}
            </SheetTitle>
            {product?.description && (
              <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{product.description}</p>
            )}
          </div>
          <button
            onClick={close}
            className="w-9 h-9 grid place-items-center rounded-full bg-white/5 hover:bg-white/10 shrink-0"
            data-testid="customize-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </SheetHeader>

        {loading ? (
          <div className="flex-1 grid place-items-center">
            <div className="w-8 h-8 rounded-full border-2 border-pink-500/40 border-t-pink-500 animate-spin" />
          </div>
        ) : !product ? null : (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Hero image */}
            {product.image_paths?.[0] && (
              <div className="aspect-video rounded-2xl overflow-hidden bg-zinc-900">
                <img
                  src={fileUrl(product.image_paths[0])}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Variations */}
            {product.variations?.length > 0 && (
              <section data-testid="section-variations">
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-3">
                  Size / Variation
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {product.variations.map((v) => {
                    const active = v.name === variationName;
                    const delta = Number(v.price_delta_cents || v.price_delta || 0);
                    return (
                      <button
                        key={v.name}
                        onClick={() => setVariationName(v.name)}
                        className={`text-left px-4 py-3 rounded-2xl border transition ${
                          active
                            ? "bg-pink-500/20 border-pink-500/60 text-white"
                            : "bg-white/[0.03] border-white/10 text-zinc-300 hover:border-white/20"
                        }`}
                        data-testid={`variation-${v.name}`}
                      >
                        <div className="text-sm font-semibold">{v.name}</div>
                        <div className="text-xs opacity-80 mt-0.5">
                          {delta ? signedFmt(delta) : "Included"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Modifier groups */}
            {product.modifier_groups?.map((g) => {
              const picks = modState[g.group_id] || {};
              const err = groupValidation[g.group_id];
              const selType = g.selection || "single";
              return (
                <section key={g.group_id} data-testid={`section-modifier-${g.group_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-bold text-white">{g.name}</h3>
                    <span className={`text-xs ${err ? "text-red-400" : "text-zinc-500"}`}>
                      {err || g.instruction || (g.required ? "Required" : "Optional")}
                    </span>
                  </div>
                  {g.description && <p className="text-xs text-zinc-500 mb-2">{g.description}</p>}
                  <div className="space-y-1.5 mt-2">
                    {(g.options || []).filter((o) => o.is_available).map((o) => {
                      const q = Number(picks[o.option_id] || 0);
                      const price = Number(o.price_adjust_cents || 0);
                      const maxQty = Number(o.max_qty || 1);
                      return (
                        <div
                          key={o.option_id}
                          className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border ${
                            q > 0
                              ? "border-pink-500/60 bg-pink-500/10"
                              : "border-white/10 bg-white/[0.03]"
                          }`}
                          data-testid={`modifier-opt-${o.option_id}`}
                        >
                          <button
                            onClick={() => toggleModOption(g.group_id, o.option_id, selType, g.max_select, maxQty)}
                            className="text-left flex-1 min-w-0"
                          >
                            <div className="text-sm font-medium truncate">{o.name}</div>
                            {price !== 0 && (
                              <div className="text-xs text-zinc-400 mt-0.5">{signedFmt(price)}</div>
                            )}
                          </button>
                          {q > 0 && maxQty > 1 && (
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => bumpModQty(g.group_id, o.option_id, -1, maxQty, g.max_select)}
                                className="w-7 h-7 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"
                                data-testid={`modifier-dec-${o.option_id}`}
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="w-5 text-center text-sm font-semibold">{q}</span>
                              <button
                                onClick={() => bumpModQty(g.group_id, o.option_id, 1, maxQty, g.max_select)}
                                className="w-7 h-7 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"
                                data-testid={`modifier-inc-${o.option_id}`}
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}

            {/* Ingredients */}
            {product.ingredients?.length > 0 && (
              <section data-testid="section-ingredients">
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-3">
                  Ingredients
                </h3>
                <div className="space-y-1.5">
                  {product.ingredients.filter((i) => i.is_available).map((ing) => {
                    const s = ingState[ing.ingredient_id] || { mode: "default", qty: 1 };
                    const modes = [
                      { k: "default", label: "Regular", show: true },
                      { k: "no", label: "No", show: ing.is_removable },
                      { k: "light", label: "Light", show: ing.allow_light },
                      { k: "extra", label: "Extra", show: ing.allow_extra },
                    ].filter((m) => m.show);
                    const extraPrice = Number(ing.extra_price_cents || 0);
                    return (
                      <div
                        key={ing.ingredient_id}
                        className="px-4 py-3 rounded-xl border border-white/10 bg-white/[0.03]"
                        data-testid={`ingredient-${ing.ingredient_id}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm font-medium truncate">{ing.name}</div>
                          {s.mode === "extra" && extraPrice > 0 && (
                            <span className="text-xs text-pink-300">
                              +{dollarFmt(extraPrice * (s.qty || 1))}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {modes.map((m) => (
                            <button
                              key={m.k}
                              onClick={() => setIngredientMode(ing.ingredient_id, m.k)}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                                s.mode === m.k
                                  ? "bg-pink-500 text-white"
                                  : "bg-white/5 text-zinc-300 hover:bg-white/10"
                              }`}
                              data-testid={`ing-mode-${ing.ingredient_id}-${m.k}`}
                            >
                              {m.label}
                            </button>
                          ))}
                          {s.mode === "extra" && (ing.max_amount || 2) > 1 && (
                            <div className="ml-auto flex items-center gap-1.5">
                              <button
                                onClick={() => bumpExtra(ing.ingredient_id, -1, ing.max_amount || 2)}
                                className="w-6 h-6 grid place-items-center rounded-full bg-white/10"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="w-4 text-center text-xs font-semibold">{s.qty}</span>
                              <button
                                onClick={() => bumpExtra(ing.ingredient_id, 1, ing.max_amount || 2)}
                                className="w-6 h-6 grid place-items-center rounded-full bg-white/10"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Special instructions */}
            {product.special_instructions_enabled && (
              <section data-testid="section-instructions">
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Special instructions
                </h3>
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value.slice(0, product.special_instructions_max_chars || 200))}
                  rows={3}
                  placeholder="E.g. no onion, allergy to peanuts…"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-sm placeholder-zinc-500 focus:border-pink-500/60 focus:outline-none resize-none"
                  data-testid="special-instructions-input"
                />
                <p className="text-[11px] text-zinc-500 mt-1 text-right">
                  {instructions.length}/{product.special_instructions_max_chars || 200}
                </p>
              </section>
            )}

            {validationError && (
              <div className="rounded-xl bg-red-500/10 border border-red-500/40 text-red-200 text-sm px-4 py-3" data-testid="customize-error">
                {validationError}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        {product && (
          <div className="px-6 py-5 border-t border-white/10 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-400">Quantity</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="w-8 h-8 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"
                  data-testid="qty-dec"
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span className="w-6 text-center font-bold" data-testid="qty-value">{quantity}</span>
                <button
                  onClick={() => {
                    const max = Number(product.max_order_quantity || 0);
                    setQuantity((q) => (max ? Math.min(max, q + 1) : q + 1));
                  }}
                  className="w-8 h-8 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"
                  data-testid="qty-inc"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <Button
              disabled={!canAdd || adding}
              onClick={onAdd}
              className="w-full h-14 rounded-full text-base font-bold bg-pink-500 hover:bg-pink-400 text-white glow-pink disabled:opacity-40 disabled:bg-zinc-800 disabled:glow-pink-none"
              data-testid="customize-add-btn"
            >
              {adding ? "Adding…" : `Add ${quantity} to bag · ${dollarFmt(livePriceCents * quantity)}`}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
